import type { Product } from "@/components/marketplace/types"
import type { Result as SearchProduct } from "@/generated/api/types.gen"
import { DEFAULT_CURRENCY, moneyFromNumber } from "@/lib/money"
import { toPriceView } from "@/lib/money/price-view"
import { buildRoute, localizeHref } from "@/lib/routes"

export type SearchPricing = Pick<Product, "price" | "was" | "payments" | "installments" | "financingMode" | "paymentCadence" | "downPayment" | "discount" | "chips">

export const deriveSearchPricing = (product: SearchProduct): SearchPricing => {
  const view = toPriceView(product.resolvedPrice)
  const plan = view?.installment
  const chips: ("freeship" | "noint")[] = []
  if (product.resolvedPrice?.free_shipping) chips.push("freeship")
  if (plan ? plan.interestFree : product.badgeTypes.includes("MARKETPLACE_ZERO_APR")) chips.push("noint")
  const merchandising = chips.length > 0 ? chips : undefined
  if (!view) return { price: moneyFromNumber(product.price ?? 0, DEFAULT_CURRENCY), chips: merchandising }
  return {
    price: view.amount,
    was: view.compareAt ?? undefined,
    payments: plan?.amounts[0],
    installments: plan?.count,
    financingMode: plan?.financingMode,
    paymentCadence: plan?.paymentCadence,
    downPayment: plan?.downPayment ?? undefined,
    discount: view.savingsPercent > 0 ? view.savingsPercent : undefined,
    chips: merchandising,
  }
}

export const mapSearchProduct = (product: SearchProduct, countryCode: string): Product => ({
  id: product.productGroupUuid,
  title: product.name,
  ...deriveSearchPricing(product),
  image: product.icon.imageUrl ?? "",
  rating: product.averageRating ?? undefined,
  reviewCount: product.countResponses ?? undefined,
  href: localizeHref(buildRoute.productFromUrl(product.productUrl), countryCode),
  serveId: product.serveId ?? undefined,
})
